import React, { useState, useEffect, createContext, useContext, useRef, useCallback, lazy, Suspense } from "react";
import { LogoutFailureAlert } from './components/auth/LogoutFailureAlert';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import "@/App.css";
import "./i18n"; // Import i18n configuration

// ─── Chunk-load error recovery — install FIRST, before any lazy
//     import has a chance to fail. Handles the "Unexpected token '<'"
//     / ChunkLoadError case that fires when a long-lived tab's
//     bundle.js references a chunk filename the server no longer has.
//     Implements a one-time reload with session cooldown so we never
//     loop. See `utils/chunkErrorRecovery.js` for details. ─────────
import { installChunkErrorRecovery } from "@/utils/chunkErrorRecovery";
installChunkErrorRecovery();

// ─── Eager imports: critical path (landing, auth, shell) ───
import PremiumHomePage from "@/components/PremiumHomePage";
import { isNative } from "@/native/platform";
import { bootstrapNative } from "@/native/bootstrap";
import { shouldProbeSession } from "@/utils/sessionRestore";
import { automaticCookieRestoreBlocked, blockAutomaticCookieRestore, allowExplicitLogin, restoredIdentityMatches } from "@/utils/sessionIsolation";
import { portalPathForUser } from "@/lib/portalPath";
import WelcomeScreen from "@/components/WelcomeScreen";
import AuthCallback from "@/components/AuthCallback";
const NativeAuthBridge = lazy(() => import("@/components/auth/NativeAuthBridge"));
import PremiumAuthPage from "@/components/auth/PremiumAuthPage";
// Phase 7g.15.48 (Rick 2026-05-27): BetaBuildBadge fully retired — caused
// a persistent overlay at #root > div:nth-child(3) on production.
// import BetaBuildBadge from "@/components/shared/BetaBuildBadge";
import { ThemeProvider } from "@/contexts/ThemeProvider";
import { LanguageProvider } from "@/context/LanguageContext";
import LanguageAutoDetectToast from "@/components/shared/LanguageAutoDetectToast";
import { PlatformReviewPrompt } from "@/components/shared/PlatformReviewPrompt";
import { PUBLIC_BOOKING_ENABLED, PUBLIC_VIP_ENABLED } from "@/config/launchFlags";
// Sonner toaster — mounted globally so any toast.error() / toast.success()
// from anywhere in the app actually appears on screen. Without this,
// `toast.error("...")` calls are silent (root cause of Codex's
// "ASAP_UNAVAILABLE has no visible message" bug).
import { Toaster } from "sonner";

// ─── Pre-launch waitlist (eager — small, served on every gated booking link) ───
import CustomerWaitlistPage from "@/components/CustomerWaitlistPage";

// ─── Lazy imports: Admin chunk ───
const AdminDashboard = lazy(() => import("@/components/admin/AdminDashboard"));

// ─── Lazy imports: Vendor chunk ───
const VendorApp = lazy(() => import("@/components/vendor/VendorApp"));
const ContractorOnboarding = lazy(() => import("@/components/vendor/ContractorOnboarding"));
const ContractorApplicationStatus = lazy(() => import("@/components/vendor/ContractorApplicationStatus"));
const VendorOnboarding = lazy(() => import("@/components/vendor/VendorOnboarding"));
const VendorStore = lazy(() => import("@/components/vendor/VendorStore"));
// VOQA-P0-02 (2026-06-18) — public Stripe Connect return landing
const VendorStripeConnectReturn = lazy(() => import("@/components/vendor/VendorStripeConnectReturn"));
const VendorActionFlowScreen = lazy(() => import("@/components/shared/VendorActionFlowScreen"));

// ─── Lazy imports: Customer chunk ───
const CustomerApp = lazy(() => import("@/components/customer/CustomerApp"));
const SimpleBookingFlow = lazy(() => import("@/components/customer/SimpleBookingFlow"));
const BookingConfirmation = lazy(() => import("@/components/customer/BookingConfirmation"));
const SubscriptionPage = lazy(() => import("@/components/customer/SubscriptionPage"));
const PostServiceTip = lazy(() => import("@/components/customer/PostServiceTip"));
const FleetContactForm = lazy(() => import("@/components/customer/FleetContactForm"));
const ServiceHistoryPage = lazy(() => import("@/components/customer/ServiceHistoryPage"));
const ShopFinder = lazy(() => import("@/components/customer/ShopFinder"));
const VendorMarketplace = lazy(() => import("@/components/customer/VendorMarketplace"));
const VendorCoveragePage = lazy(() => import("@/components/VendorCoveragePage"));
const CustomerLiveTrackingScreen = lazy(() => import("@/components/shared/CustomerLiveTrackingScreen"));
const BookingSuccess = lazy(() => import("@/components/customer/BookingSuccess"));

// ─── Lazy imports: Content / static pages ───
const SplashScreen = lazy(() => import("@/components/SplashScreen"));
const FAQPage = lazy(() => import("@/components/FAQPage"));
const CustomerQRPage = lazy(() => import("@/components/CustomerQRPage"));
const VendorQRPage = lazy(() => import("@/components/VendorQRPage"));
// IDA-P0-02 (2026-06-18, Investor Hardening Batch C deferred) —
// Public investor preview snapshot.
const InvestorPreview = lazy(() => import("@/components/InvestorPreview"));
const BlogPage = lazy(() => import("@/components/BlogPage").then(m => ({ default: m.BlogListPage })));
const BlogPostPage = lazy(() => import("@/components/blog/BlogPostDispatcher"));
const AboutUsPage = lazy(() => import("@/components/AboutUsPage"));
const PublicStatusPage = lazy(() => import("@/components/PublicStatusPage"));
const TermsOfServicePage = lazy(() => import("@/components/TermsOfServicePage"));
// Phase 7g.15.15 (Rick directive 2026-05-15) — Copyright Infringement
// Notification Policy. Original WashPros legal copy.
const CopyrightPolicyPage = lazy(() => import("@/components/CopyrightPolicyPage"));
// Phase 7g.15.20i (Rick directive 2026-05-16, post-DBA filing) — full
// WashPros Legal & Trust Policy Center: hub + 16 individual policy
// pages. The 16 individual pages are tiny wrappers around the shared
// PolicyPageShell + policyContent module, so they're imported directly
// (non-lazy) to avoid 16 tiny chunks. The hub stays lazy.
const LegalHubPage = lazy(() => import("@/components/legal/LegalHubPage"));
const PrivacyPolicyPage = lazy(() => import("@/components/PrivacyPolicyPage"));
// Phase 7g.15.48 (Rick 2026-05-27, second pass) — public SMS consent
// receipt page for Twilio A2P 10DLC campaign reviewers. NEVER collects
// opt-in here — this is only the documentation surface.
const SmsConsentPage = lazy(() => import("@/components/SmsConsentPage"));
const ContactUsPage = lazy(() => import("@/components/ContactUsPage"));
const VIPSignupPage = lazy(() => import("@/components/VIPSignupPage"));
const AccountDeletionPublic = lazy(() => import("@/components/AccountDeletionPublic"));
const VideoGalleryPage = lazy(() => import("@/components/VideoGalleryPage"));
const PricingPage = lazy(() => import("@/components/PricingPage"));
const BecomeAWashProPage = lazy(() => import("@/components/BecomeAWashProPage"));
// BrandUsagePage + BrandAssetsPage are admin-only now — imported inside AdminDashboard, not lazy-loaded here.
const SupportChatbot = lazy(() => import("@/components/shared/SupportChatbot"));
const BetaWelcomeCard = lazy(() => import("@/components/beta/BetaWelcomeCard"));

// ─── Lazy imports: Service pages ───
const BoatServicesPage = lazy(() => import("@/components/services/BoatServicesPage"));
const RVServicesPage = lazy(() => import("@/components/services/RVServicesPage"));
const FleetServicesPage = lazy(() => import("@/components/services/FleetServicesPage"));
const CarWashServicesPage = lazy(() => import("@/components/services/CarWashServicesPage"));
const CeramicCoatingPage = lazy(() => import("@/components/services/CeramicCoatingPage"));
const CommercialFleetPage = lazy(() => import("@/components/services/CommercialFleetPage"));

// ─── Lazy imports: Legal pages ───
const IndependentContractorAgreement = lazy(() => import("@/components/legal/IndependentContractorAgreement"));
const MobileVendorBusinessAgreement = lazy(() => import("@/components/legal/MobileVendorBusinessAgreement"));
const BrickAndMortarShopAgreement = lazy(() => import("@/components/legal/BrickAndMortarShopAgreement"));
const VendorAgreementsHub = lazy(() => import("@/components/legal/VendorAgreementsHub"));
// Phase 7g.15.20i — Trust Policy Center wrappers (eager, share shell)
import {
  AcceptableUsePage,
  VendorMarketplaceStandardsPage,
  BookingRefundDisputePolicyPage,
  CommunicationsPolicyPage,
  DataSecurityPage,
  CookiePolicyPage,
  DataRetentionPage,
  ReviewsPolicyPage,
  InfluencerDisclosurePage,
  TrademarkPolicyPage,
  SubscriptionTermsPage,
  VendorPayoutPolicyPage,
  AIDisclosurePage,
  PhotoMediaPolicyPage,
  PropertyDamagePolicyPage,
  DMCAAgentPage,
} from "@/components/legal/SimplePolicyPages";
const VendorEquipmentListPage = lazy(() => import("@/pages/VendorEquipmentListPage"));
const FranchisePage = lazy(() => import("@/components/FranchisePage"));
const ServiceLandingPage = lazy(() => import("@/components/seo/ServiceLandingPage"));
const FloridaHubPage = lazy(() => import("@/components/seo/FloridaHubPage"));
const FloridaServicePage = lazy(() => import("@/components/seo/FloridaServicePage"));
const FloridaCountyPage = lazy(() => import("@/components/seo/FloridaCountyPage"));
const FloridaCountyOrServiceDispatch = lazy(() => import("@/components/seo/FloridaCountyOrServiceDispatch"));
const FloridaCityHubPage = lazy(() => import("@/components/seo/FloridaCityHubPage"));
const VendorLandingPage = lazy(() => import("@/components/vendorSeo/VendorLandingPage"));
const LogoAnimationReviewPage = lazy(() => import("@/components/LogoAnimationReviewPage"));

// Phase 7c slug rename: 301-equivalent SPA redirect that preserves
// the captured `:citySlug` so /florida/miami/fleet-partner →
// /florida/miami/fleet-partner-opportunities. Inlined (not its own
// file) because it's a 5-line helper.
function FleetPartnerSlugRedirect() {
  const { citySlug } = useParams();
  return (
    <Navigate
      to={`/florida/${citySlug}/fleet-partner-opportunities`}
      replace
    />
  );
}

// CBQA-P2-08 (2026-06-17) — `/dashboard/book` alias → public `/book`
// flow with all query params preserved. The customer-facing
// confirmation page already routes "Wash another vehicle" to
// `/dashboard/book?from_booking=…`; previously this dead-ended at
// /welcome when the user was unauthenticated. Now it routes to the
// public booking flow with the original query string intact.
function DashboardBookAlias() {
  const loc = useLocation();
  const target = `/book${loc.search || ""}${loc.hash || ""}`;
  return <Navigate to={target} replace />;
}

// VOQA-P0-01 (2026-06-18) — `/vendor/onboarding` legacy alias →
// canonical `/vendor/apply` flow with all query params (especially
// `?tier=`) preserved. The VendorQRPage tier-click navigation now
// targets `/vendor/apply` directly, but old QR codes in the wild
// and any bookmarks/emails still point at this legacy URL — this
// keeps them working.
function VendorApplyAlias() {
  const loc = useLocation();
  const target = `/vendor/apply${loc.search || ""}${loc.hash || ""}`;
  return <Navigate to={target} replace />;
}

// ─── Lazy imports: Auth pages (non-critical path) ───
const AuthPage = lazy(() => import("@/components/auth/EmailAuth").then(m => ({ default: m.AuthPage })));
const MagicLinkCallback = lazy(() => import("@/components/auth/MagicLinkCallback"));
const PasswordResetPage = lazy(() => import("@/components/auth/PasswordResetPage"));
const SetupPasswordPage = lazy(() => import("@/components/auth/SetupPasswordPage"));

// ─── Suspense fallback ───
const PageLoader = () => {
  // Inline-import to avoid pulling AnimatedWashProsDroplets into the
  // initial bundle eagerly; this is only rendered while a route chunk
  // loads, so a tiny extra resolution roundtrip is fine.
  // Static import is safe here because the component is < 6 KB.
  // (Keeping this comment so future readers don't try to "optimise"
  // it away into a lazy load that races the Suspense itself.)
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <PageLoaderInner />
        <p className="text-slate-400 text-sm">Loading...</p>
      </div>
    </div>
  );
};
// Lazy-imported separately so the Suspense fallback never crashes if
// the chunk fails — falls back to a simple cyan div.
const AnimatedWashProsDroplets = lazy(() => import("@/components/brand/AnimatedWashProsDroplets"));
const PageLoaderInner = () => (
  <Suspense fallback={<div className="w-12 h-12 border-4 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin" />}>
    <AnimatedWashProsDroplets variant="loader" size="md" />
  </Suspense>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance.
// NOTE: Emergent's ingress always returns `Access-Control-Allow-Origin: *`, so
// withCredentials must remain false. Auth flows rely on Bearer tokens stored in
// sessionStorage and attached via the request interceptor below.
export const apiClient = axios.create({
  baseURL: API,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json'
  }
});

// An explicit credential is authoritative, even when empty/malformed. Pin auth
// operations to the credential captured at invocation, not a later storage read.
apiClient.interceptors.request.use((config) => {
  let token = null;
  try {
    token = Object.prototype.hasOwnProperty.call(config, 'sessionCredential')
      ? config.sessionCredential : sessionStorage.getItem('session_token');
  } catch { /* no implicit cookie identity */ }
  config.headers = config.headers || {};
  const authorizationKey = Object.keys(config.headers).find(key => key.toLowerCase() === 'authorization');
  const hasExplicitCredential = authorizationKey !== undefined;
  const newAuthentication = ['/auth/email/login','/auth/email/signup','/auth/session','/auth/apple/signin',
    '/auth/phone/verify-code','/auth/magic-link/verify'].includes((config.url || '').split('?')[0]);
  const blocked = automaticCookieRestoreBlocked({ explicitToken: Boolean(token) });
  if (newAuthentication) {
    if (hasExplicitCredential) delete config.headers[authorizationKey];
  } else if (token && !hasExplicitCredential && !blocked) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  // withCredentials=false alone still sends SAME-origin cookies in XHR. Fetch
  // omit also ignores Set-Cookie, so a delayed Bearer logout cannot erase a new
  // browser login cookie. Cookie-only browser restoration keeps its own path.
  if (newAuthentication || hasExplicitCredential || token || blocked) {
    config.adapter = 'fetch';
    config.withCredentials = false;
    config.fetchOptions = { ...config.fetchOptions, credentials: 'omit' };
  }
  return config;
});

// Response interceptor: clear cached user on 401 (cookie is managed by browser)
// Auth state is managed solely by AuthProvider.checkAuth()

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // NEVER clear auth state from the response interceptor.
    // Auth state is managed SOLELY by AuthProvider's checkAuth() function.
    // Background 401s from notification polling, AB tests, or WebSocket
    // must NOT wipe the user mid-session or mid-checkout.
    return Promise.reject(error);
  }
);

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [logoutFailure, setLogoutFailure] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const logoutRequestRef = useRef(null);
  // Cached identity is only a restoration hint, never an authorization grant.
  const [user, setUser] = useState(null);
  // Reactive session token mirror — `useWebSocket` and other
  // hooks key WebSocket reconnect lifecycles off this. Without
  // it (P0 fix 2026-05-07 war-room audit) every realtime
  // connection would silently fail because `sessionToken` came
  // back undefined from `useAuth()`.
  const [sessionToken, setSessionToken] = useState(() => {
    try {
      return sessionStorage.getItem("session_token") || null;
    } catch (_) {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  // Track if we just logged in to prevent checkAuth from overriding redirect
  const justLoggedInRef = useRef(false);
  const authEpochRef = useRef(0);

  // No 401 interceptor callback needed — checkAuth is the sole auth controller

  const checkAuth = useCallback(async () => {
    const authEpoch = authEpochRef.current;
    // CRITICAL: Skip checkAuth if we just logged in (prevents race condition)
    if (justLoggedInRef.current) {
      setLoading(false);
      return;
    }
    
    // Skip the /auth/me probe entirely when there's no token in storage —
    // this prevents the browser console from logging a benign 401 on every
    // public page load (homepage, waitlist, vendor application, etc).
    //
    // NATIVE COLD-START FIX (Samsung S21 QA 2026-07-20): on a native cold
    // start `sessionStorage` is empty until Capacitor Preferences are
    // hydrated back into it by `bootstrapNative()` (which runs async so
    // React can mount instantly). If checkAuth read the token BEFORE that
    // hydration finished, it saw no token, wiped the logged-in user, and
    // stranded them on the public marketing/waitlist page. Await the
    // (idempotent, timeout-capped, never-rejecting) hydration first. On
    // web this resolves synchronously (no-op) so login is unaffected.
    try {
      await bootstrapNative();
    } catch (_e) { /* best-effort — proceed regardless */ }
    if (authEpoch !== authEpochRef.current) return;

    let token = null;
    let cachedUser = null;
    try {
      token = sessionStorage.getItem('session_token');
      // Keep-me-logged-in persists an explicit Bearer, not an implicit cookie.
      // Restore it only when opted in and the explicit-exit latch permits it.
      if (!token && localStorage.getItem('wp_keep_logged_in') === '1'
          && !automaticCookieRestoreBlocked({ explicitToken: true })) {
        const persistedToken = localStorage.getItem('session_token');
        if (persistedToken) {
          sessionStorage.setItem('session_token', persistedToken);
          token = persistedToken;
        }
      }
      cachedUser = localStorage.getItem('user');
    } catch { /* Storage access can be disabled; cookie restoration still works. */ }
    if (!shouldProbeSession({ token, native: isNative(), pathname: window.location.pathname,
      origin: window.location.origin, backendUrl: BACKEND_URL, cachedUser,
      cookieRestoreBlocked: automaticCookieRestoreBlocked({ explicitToken: Boolean(token) }) })) {
      setUser(null);
      setSessionToken(null);
      setLoading(false);
      return;
    }

    try {
      const response = await apiClient.get("/auth/me", { sessionCredential: token });
      if (authEpoch !== authEpochRef.current) return;
      if (!restoredIdentityMatches(cachedUser,response.data)) {
        throw Object.assign(new Error('Explicit sign-in required for changed session identity'), {response:{status:401}});
      }
      setUser(response.data);
      setSessionToken(token);
      // Update localStorage with fresh data
      try { localStorage.setItem("user", JSON.stringify(response.data)); } catch { /* Keep the verified React state. */ }
    } catch (error) {
      // Only clear the session when the backend EXPLICITLY says "not authenticated".
      // Any other failure (network blip, 429 rate-limit, transient 5xx, CORS hiccup)
      // must NOT nuke the cached user — doing so caused session-loss during rapid
      // route navigation (observed in Phase 1 audit: after ~26 admin routes in a
      // tight loop the user would get wiped and redirected to /welcome).
      if (authEpoch !== authEpochRef.current) return;
      const status = error?.response?.status;
      const isTrueAuthFailure = status === 401;
      if (isTrueAuthFailure) {
        authEpochRef.current += 1;
        blockAutomaticCookieRestore();
        setUser(null);
        setSessionToken(null);
        for (const [storage, key] of [[localStorage, 'user'], [localStorage, 'session_token'], [sessionStorage, 'session_token']]) {
          try { storage.removeItem(key); } catch { /* The exit latch remains authoritative. */ }
        }
      }
      // For non-401 errors, keep whatever user state we already have so a
      // brief network hiccup doesn't feel like a forced logout to the user.
    } finally {
      if (authEpoch === authEpochRef.current || automaticCookieRestoreBlocked()) setLoading(false);
    }
  }, []);

  // Custom setUser that also marks login state
  const setUserWithLoginFlag = useCallback((userData, isLogin = false) => {
    if (userData && !isLogin && automaticCookieRestoreBlocked()) return;
    if (isLogin && userData) {
      authEpochRef.current += 1;
      allowExplicitLogin();
      try { setSessionToken(sessionStorage.getItem('session_token')); } catch { /* Existing explicit state remains. */ }
      justLoggedInRef.current = true;
      // Reset the flag after a longer delay to allow admin dashboard to fully load
      setTimeout(() => { justLoggedInRef.current = false; }, 10000);
    }
    // Mirror user into localStorage so ProtectedRoute's localStorage
    // fallback can recover when React's state batching hasn't yet
    // flushed the new user into the route's render path. Without
    // this, fresh signups race the navigate("/dashboard") and get
    // bounced to /welcome (Rick QA 2026-05-08 — iter_268 finding,
    // root cause of the testing-agent UI auth gate failure).
    try {
      if (userData) {
        localStorage.setItem("user", JSON.stringify(userData));
      } else {
        localStorage.removeItem("user");
      }
    } catch (e) {
      // localStorage quota / private-mode / disabled — non-fatal,
      // React state still has the user; just lose the cross-render
      // fallback safety net.
    }
    setUser(userData);
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // Check both hash (for older OAuth flows) and pathname (for callback route)
    if (window.location.hash?.includes('session_id=') || 
        window.location.pathname?.includes('/auth/callback') ||
        window.location.search?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    //
    // Three branches:
    //   1. Native (Capacitor iOS/Android): open the Emergent Auth URL
    //      in Custom Tabs / SFSafariViewController via @capacitor/browser
    //      so Google does NOT see an embedded WebView and reject with
    //      403 disallowed_useragent. The redirect comes back via the
    //      App Link / Universal Link wired in capacitor.config.ts +
    //      AndroidManifest + Info.plist → appUrlOpen → AuthCallback.
    //   2. Embedded webview on web (Emergent preview shell, Facebook
    //      in-app browser, Instagram, etc.): show a friendly toast
    //      pointing the user to a real browser instead of letting
    //      Google show its raw 403 error page.
    //   3. Real browser on web (Chrome/Safari/Edge/Firefox/Samsung
    //      Internet): unchanged window.location.href redirect.
    //
    // Phase 7g.15.67 (Dan QA #6) — iPad "Invalid state parameter"
    // root cause: on Capacitor iOS the in-app webview origin is
    // `washpros://localhost`, so passing that as Emergent's redirect
    // means Emergent's state cookie (set on auth.emergentagent.com)
    // is validated against a URL it cannot actually reach.  Solution:
    // on native we use the public production WEB origin as the
    // bridge, then `/auth/native-bridge` on web bounces the user back
    // into the app via the `washpros://` custom scheme so the native
    // appUrlOpen listener forwards session_id to AuthCallback.
    // Web behaviour (#2 and #3) is unchanged.
    // Lazy import to keep web-only bundle slim
    import("@/native/platform").then(({ isNative }) => {
      if (isNative()) {
        // Native OAuth bridge base URL — MUST come from env vars so it
        // matches whichever environment (preview, production, custom
        // domain) the AAB/IPA was built against. No hardcoded fallback
        // here: REACT_APP_BACKEND_URL is a protected env var that is
        // always present at build time, so the previous emergent.host
        // hardcoded fallback was unreachable in practice — removed to
        // satisfy the Emergent deployment "no fallback URLs" rule.
        const nativeBridgeBase =
          process.env.REACT_APP_NATIVE_OAUTH_BRIDGE_URL ||
          process.env.REACT_APP_BACKEND_URL;
        const nativeRedirect = `${nativeBridgeBase.replace(/\/+$/, "")}/auth/native-bridge`;
        const nativeAuthUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(nativeRedirect)}`;
        import("@/native/browser").then(({ openExternal }) => {
          openExternal(nativeAuthUrl).catch((e) => {
            console.error("Native Browser.open failed", e);
            window.location.href = nativeAuthUrl; // fallback
          });
        });
        return;
      }
      const redirectUrl = window.location.origin;
      const emergentAuthUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      import("@/native/embeddedWebview").then(({ isEmbeddedWebView, getOpenInBrowserHint }) => {
        if (isEmbeddedWebView()) {
          const hint = getOpenInBrowserHint();
          import("sonner").then(({ toast }) => {
            toast.error(hint.title, {
              description: hint.message,
              duration: 12000,
              action: {
                label: "Copy URL",
                onClick: () => {
                  try { navigator.clipboard?.writeText(hint.url); } catch (_) { /* noop */ }
                },
              },
            });
          });
          return;
        }
        // Real browser — unchanged behavior
        window.location.href = emergentAuthUrl;
      });
    });
  };

  const logout = async () => {
    const authEpoch = ++authEpochRef.current;
    let token = null;
    try { token = sessionStorage.getItem('session_token'); } catch { /* Cookie-only session. */ }
    try {
      await apiClient.post("/auth/logout", null, { sessionCredential: token, timeout: 10000 });
    } catch (error) {
      if (error?.response?.status !== 401) throw error;
    }
    if (authEpoch !== authEpochRef.current) return false;
    // Invalidate probes started DURING the pending request as well as before it.
    authEpochRef.current += 1;
    blockAutomaticCookieRestore();
    // Clear ALL auth-related client state (Item 7 of security policy)
    for (const key of ['session_token', 'user', 'vendor_session', 'admin_session', 'login_timestamp',
      'washpros_pending_booking', 'intendedPath', 'portal_switch_intent']) {
      for (const storage of [sessionStorage, localStorage]) {
        try { storage.removeItem(key); } catch { /* Clear other stores even if one fails. */ }
      }
    }
    setSessionToken(null);
    setUser(null);
    // Drop axios default Authorization header
    if (apiClient?.defaults?.headers?.common) {
      delete apiClient.defaults.headers.common['Authorization'];
    }
    return true;
  };

  const requestLogout = () => {
    if (logoutRequestRef.current?.epoch === authEpochRef.current) return logoutRequestRef.current.promise;
    setLoggingOut(true);
    const operation = logout();
    const epoch = authEpochRef.current;
    const promise = operation.then((ok) => {
      if (ok === false) return false;
      setLogoutFailure(false);
      return true;
    }).catch(() => {
      if (epoch === authEpochRef.current) setLogoutFailure(true);
      return false;
    }).finally(() => {
      if (logoutRequestRef.current?.promise === promise) {
        logoutRequestRef.current = null;
        setLoggingOut(false);
      }
    });
    logoutRequestRef.current = { epoch, promise };
    return promise;
  };

  useEffect(() => { setLogoutFailure(false); }, [user?.user_id]);

  return (
    <AuthContext.Provider value={{ user, setUser: setUserWithLoginFlag, sessionToken, setSessionToken, loading, login, logout: requestLogout, requestLogout, checkAuth }}>
      {children}
      <LogoutFailureAlert open={logoutFailure} retrying={loggingOut} onRetry={requestLogout} />
    </AuthContext.Provider>
  );
};

// Root "/" route. On WEB it always renders the public marketing homepage.
// On NATIVE it must never strand a logged-in user on the public page
// (Samsung S21 cold-start QA 2026-07-20): while the session is being
// restored it shows a "Restoring your session…" state, then routes to the
// correct portal; only a genuinely logged-out user sees the homepage.
const RootRoute = () => {
  const { user, loading } = useAuth();
  if (isNative()) {
    let hasCachedSession = !!user;
    if (!hasCachedSession) {
      try { hasCachedSession = !!localStorage.getItem("user"); } catch (_e) { /* noop */ }
    }
    if (loading && hasCachedSession) {
      return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center" data-testid="session-restoring">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin" />
            <p className="text-slate-400">Restoring your session…</p>
          </div>
        </div>
      );
    }
    if (!loading && user) {
      return <Navigate to={portalPathForUser(user)} replace />;
    }
  }
  return <PremiumHomePage />;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center" data-testid="protected-session-loading">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Only the verified AuthProvider state grants access. Neither navigation state
  // nor a cached user grants a role or traps a failed restoration in a spinner.
  if (!user) {
    const returnPath = location.pathname + location.search;
    try { localStorage.setItem('intendedPath', returnPath); } catch { /* Optional navigation hint. */ }
    try { sessionStorage.setItem('intendedPath', returnPath); } catch { /* Optional navigation hint. */ }
    return <Navigate to={`/welcome?redirect=${encodeURIComponent(returnPath)}`} replace />;
  }

  return children;
};

// Special wrapper for /dashboard route
// Only redirects users who accidentally land here after login (not via portal switcher)
const CustomerDashboardRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const allowedRef = React.useRef(false);

  // Persist the portal switch intent across re-renders using a ref
  if (!allowedRef.current) {
    const portalSwitchIntent = sessionStorage.getItem("portal_switch_intent");
    if (portalSwitchIntent) {
      sessionStorage.removeItem("portal_switch_intent");
      allowedRef.current = true;
    }
    
    const referrer = document.referrer;
    if (referrer && (referrer.includes("/admin") || referrer.includes("/vendor"))) {
      allowedRef.current = true;
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/welcome" replace />;
  }

  // User explicitly switched portals — allow access
  if (allowedRef.current) {
    return children;
  }
  
  // Only redirect if this appears to be a fresh login redirect
  const isAdmin = ["admin", "master_admin", "super_admin"].includes(user?.role);
  const isVendor = user?.role === "vendor";
  
  const loginTimestamp = sessionStorage.getItem("login_timestamp");
  const now = Date.now();
  const justLoggedIn = loginTimestamp && (now - parseInt(loginTimestamp)) < 5000;
  
  if (justLoggedIn && isAdmin) {
    return <Navigate to="/admin" replace />;
  }
  
  if (justLoggedIn && isVendor) {
    return <Navigate to="/vendor" replace />;
  }

  // Allow access - user likely switched portals or directly navigated
  return children;
};

// Firefox Browser Warning Component
const FirefoxWarning = () => {
  const [dismissed, setDismissed] = useState(false);
  const [isFirefox, setIsFirefox] = useState(false);
  
  useEffect(() => {
    // Check if browser is Firefox
    const isFF = navigator.userAgent.toLowerCase().includes('firefox');
    const wasDismissed = sessionStorage.getItem('firefox_warning_dismissed');
    setIsFirefox(isFF && !wasDismissed);
  }, []);
  
  const handleDismiss = () => {
    sessionStorage.setItem('firefox_warning_dismissed', 'true');
    setDismissed(true);
  };
  
  if (!isFirefox || dismissed) return null;
  
  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] bg-amber-500 text-black px-4 py-2 flex items-center justify-between">
      <div className="flex items-center gap-2 flex-1">
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <span className="text-sm font-medium">
          Firefox users may experience login issues. For the best experience, we recommend using Chrome, Edge, or Safari.
        </span>
      </div>
      <button 
        onClick={handleDismiss}
        className="ml-4 px-3 py-1 bg-black/20 hover:bg-black/30 rounded text-sm font-medium transition-colors"
      >
        Dismiss
      </button>
    </div>
  );
};

// Main App Router
function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();

  // Native deep-link wiring (no-op on web). When the Emergent Auth
  // redirect comes back to the installed app via an Android App Link
  // / iOS Universal Link, the @capacitor/app appUrlOpen event fires
  // and we route the path (incl. #session_id=...) into React Router
  // so the existing AuthCallback exchange runs unchanged.
  useEffect(() => {
    let cleanup = () => {};
    let mounted = true;
    import("@/native/deeplinks").then(({ initDeepLinks }) => {
      if (!mounted) return;
      cleanup = initDeepLinks(navigate);
    });
    return () => { mounted = false; cleanup(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check URL fragment or query params for session_id - must be synchronous during render
  if (location.hash?.includes('session_id=') || location.search?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Public routes - Premium Home Page as default (eager loaded) */}
      <Route path="/" element={<RootRoute />} />
      <Route path="/home" element={<PremiumHomePage />} />
      <Route path="/welcome" element={<WelcomeScreen />} />
      <Route path="/splash" element={<SplashScreen />} />
      
      {/* QR Code Pages - Public */}
      <Route path="/join" element={<CustomerQRPage />} />
      <Route path="/join-customer" element={<CustomerQRPage />} />
      <Route path="/join-vendor" element={<VendorQRPage />} />
      <Route path="/become-vendor" element={<VendorQRPage />} />
      <Route path="/vendor/signup" element={<VendorQRPage />} />
      {/* IDA-P0-02 (2026-06-18, Investor Hardening Batch C deferred)
          Public investor preview snapshot URL. Aggregate-only data
          via /api/investor-preview/snapshot (60s cache, no PII).
          Rick can share this URL with an investor without logging
          the investor in. */}
      <Route path="/investor-preview" element={<InvestorPreview />} />
      
      {/* Contractor (Gig-Worker) Onboarding - Public */}
      <Route path="/contractor/apply" element={<ContractorOnboarding />} />
      {/* VOQA-P1-02 (2026-06-18) — `/vendors/apply` (plural) used to
          mount ContractorOnboarding, creating a route collision with
          the canonical singular `/vendor/apply` that mounts the newer
          VendorOnboarding (14-step modern flow). Marketing/QR/email
          shares to the plural URL silently forked prospects into the
          wrong flow. Aliasing the plural URL to the singular ensures
          one canonical funnel and unified backend collection. */}
      <Route path="/vendors/apply" element={<Navigate to="/vendor/apply" replace />} />
      <Route path="/become-a-vendor" element={<ContractorOnboarding />} />
      <Route path="/become-a-washpro" element={<BecomeAWashProPage />} />
      <Route path="/franchise" element={<FranchisePage />} />
      <Route path="/franchise-opportunities" element={<Navigate to="/franchise" replace />} />

      {/* ─── SEO landing pages (canonical washpros.io/{slug}) ─── */}
      {/* Service-level (10) */}
      <Route path="/mobile-car-wash" element={<ServiceLandingPage />} />
      <Route path="/mobile-detailing" element={<ServiceLandingPage />} />
      <Route path="/boat-detailing" element={<ServiceLandingPage />} />
      <Route path="/rv-wash-detailing" element={<ServiceLandingPage />} />
      <Route path="/fleet-washing" element={<ServiceLandingPage />} />
      <Route path="/golf-cart-detailing" element={<ServiceLandingPage />} />
      <Route path="/luxury-car-detailing" element={<ServiceLandingPage />} />
      <Route path="/classic-car-detailing" element={<ServiceLandingPage />} />
      <Route path="/watercraft-detailing" element={<ServiceLandingPage />} />
      <Route path="/atv-utv-detailing" element={<ServiceLandingPage />} />
      {/* Phase 7g.15.47 — Luxury, Collectible & Small Aircraft umbrella. */}
      <Route path="/luxury-collectible-aircraft-detailing" element={<ServiceLandingPage />} />
      {/* Phase 7g.15.47 QA pass — direct aircraft slug + redirect from
          /small-aircraft-detailing for SEO equity / external link consistency. */}
      <Route path="/aircraft-detailing" element={<ServiceLandingPage />} />
      <Route path="/small-aircraft-detailing" element={<Navigate to="/aircraft-detailing" replace />} />

      {/* ─── Florida programmatic SEO surface ──────────────────────
          Mounted under /florida/* — distinct from the legacy
          /mobile-car-wash and /miami-mobile-car-wash flat routes
          above. Order matters: the city-then-service pattern must
          match BEFORE the bare slug pattern so /florida/miami/...
          isn't captured as a county. We sniff "-county" suffix to
          route to the county hub.
      ───────────────────────────────────────────────────────────── */}
      <Route path="/florida" element={<FloridaHubPage />} />
      {/* Phase 7c slug rename: fleet-partner → fleet-partner-opportunities.
          SPA-side 301 equivalent so client-side <Link>s + back-buttons
          + bookmarks resolve.  Netlify _redirects file handles the
          true 301 on direct-hit page loads.  `replace` so the old
          URL doesn't pollute history.  Must come BEFORE the
          `:citySlug/:serviceSlug` and `:countySlug` patterns or
          react-router will match those first. */}
      <Route
        path="/florida/fleet-partner"
        element={<Navigate to="/florida/fleet-partner-opportunities" replace />}
      />
      <Route
        path="/florida/:citySlug/fleet-partner"
        element={<FleetPartnerSlugRedirect />}
      />
      <Route path="/florida/:citySlug/:serviceSlug" element={<FloridaServicePage />} />
      <Route path="/florida/:countySlug" element={<FloridaCountyOrServiceDispatch />} />
      {/* Local-SEO (7) */}
      <Route path="/miami-mobile-car-wash" element={<ServiceLandingPage />} />
      <Route path="/fort-lauderdale-boat-detailing" element={<ServiceLandingPage />} />
      <Route path="/orlando-fleet-washing" element={<ServiceLandingPage />} />
      <Route path="/tampa-rv-detailing" element={<ServiceLandingPage />} />
      <Route path="/naples-golf-cart-detailing" element={<ServiceLandingPage />} />
      <Route path="/jacksonville-mobile-detailing" element={<ServiceLandingPage />} />
      <Route path="/palm-beach-luxury-car-detailing" element={<ServiceLandingPage />} />
      {/* Local-SEO batch 2 (11) */}
      <Route path="/sarasota-mobile-detailing" element={<ServiceLandingPage />} />
      <Route path="/boca-raton-boat-detailing" element={<ServiceLandingPage />} />
      <Route path="/the-villages-golf-cart-detailing" element={<ServiceLandingPage />} />
      <Route path="/key-west-luxury-detailing" element={<ServiceLandingPage />} />
      <Route path="/fort-myers-boat-detailing" element={<ServiceLandingPage />} />
      <Route path="/cape-coral-boat-detailing" element={<ServiceLandingPage />} />
      <Route path="/st-petersburg-mobile-car-wash" element={<ServiceLandingPage />} />
      <Route path="/clearwater-mobile-detailing" element={<ServiceLandingPage />} />
      <Route path="/vero-beach-golf-cart-detailing" element={<ServiceLandingPage />} />
      <Route path="/coral-gables-luxury-car-detailing" element={<ServiceLandingPage />} />
      <Route path="/ocala-atv-utv-detailing" element={<ServiceLandingPage />} />

      {/* ─── SEO batch 3 — new services + 20 high-intent city combos ─── */}
      <Route path="/ceramic-coating" element={<ServiceLandingPage />} />
      <Route path="/interior-detailing" element={<ServiceLandingPage />} />
      <Route path="/miami-ceramic-coating" element={<ServiceLandingPage />} />
      <Route path="/palm-beach-ceramic-coating" element={<ServiceLandingPage />} />
      <Route path="/boca-raton-luxury-car-detailing" element={<ServiceLandingPage />} />
      <Route path="/naples-luxury-detailing" element={<ServiceLandingPage />} />
      <Route path="/sarasota-boat-detailing" element={<ServiceLandingPage />} />
      <Route path="/tampa-fleet-washing" element={<ServiceLandingPage />} />
      <Route path="/orlando-rv-detailing" element={<ServiceLandingPage />} />
      <Route path="/jacksonville-mobile-car-wash" element={<ServiceLandingPage />} />
      <Route path="/fort-lauderdale-mobile-detailing" element={<ServiceLandingPage />} />
      <Route path="/key-west-boat-detailing" element={<ServiceLandingPage />} />
      <Route path="/cape-coral-mobile-detailing" element={<ServiceLandingPage />} />
      <Route path="/clearwater-mobile-car-wash" element={<ServiceLandingPage />} />
      <Route path="/st-petersburg-fleet-washing" element={<ServiceLandingPage />} />
      <Route path="/vero-beach-luxury-detailing" element={<ServiceLandingPage />} />
      <Route path="/port-st-lucie-mobile-detailing" element={<ServiceLandingPage />} />
      <Route path="/melbourne-mobile-car-wash" element={<ServiceLandingPage />} />
      <Route path="/pensacola-boat-detailing" element={<ServiceLandingPage />} />

      {/* ─── Vendor SEO landing pages (19 — service + city) ─── */}
      <Route path="/mobile-detailing-jobs-florida" element={<VendorLandingPage />} />
      <Route path="/mobile-car-wash-leads" element={<VendorLandingPage />} />
      <Route path="/car-wash-vendor-platform" element={<VendorLandingPage />} />
      <Route path="/boat-detailing-leads-florida" element={<VendorLandingPage />} />
      <Route path="/fleet-washing-vendor-opportunities" element={<VendorLandingPage />} />
      <Route path="/rv-detailing-vendor-opportunities" element={<VendorLandingPage />} />
      <Route path="/golf-cart-detailing-vendor-opportunities" element={<VendorLandingPage />} />
      <Route path="/luxury-detailing-vendor-opportunities" element={<VendorLandingPage />} />
      <Route path="/detailing-business-opportunity-florida" element={<VendorLandingPage />} />
      <Route path="/miami-mobile-detailing-jobs" element={<VendorLandingPage />} />
      <Route path="/orlando-mobile-car-wash-jobs" element={<VendorLandingPage />} />
      <Route path="/tampa-detailing-business-opportunity" element={<VendorLandingPage />} />
      <Route path="/jacksonville-car-wash-vendor" element={<VendorLandingPage />} />
      <Route path="/fort-lauderdale-boat-detailing-jobs" element={<VendorLandingPage />} />
      <Route path="/naples-golf-cart-detailing-vendor" element={<VendorLandingPage />} />
      <Route path="/sarasota-detailing-vendor-opportunity" element={<VendorLandingPage />} />
      <Route path="/palm-beach-luxury-detailing-vendor" element={<VendorLandingPage />} />
      <Route path="/the-villages-golf-cart-detailing-vendor" element={<VendorLandingPage />} />
      <Route path="/cape-coral-boat-detailing-vendor" element={<VendorLandingPage />} />

      {/* ─── Vendor SEO batch 3 — 1 new service + 9 missing metros ─── */}
      <Route path="/ceramic-coating-vendor-opportunities" element={<VendorLandingPage />} />
      <Route path="/boca-raton-detailing-vendor" element={<VendorLandingPage />} />
      <Route path="/clearwater-detailing-vendor" element={<VendorLandingPage />} />
      <Route path="/st-petersburg-fleet-washing-vendor" element={<VendorLandingPage />} />
      <Route path="/fort-myers-detailing-vendor" element={<VendorLandingPage />} />
      <Route path="/key-west-detailing-vendor" element={<VendorLandingPage />} />
      <Route path="/port-st-lucie-detailing-vendor" element={<VendorLandingPage />} />
      <Route path="/melbourne-detailing-vendor" element={<VendorLandingPage />} />
      <Route path="/pensacola-detailing-vendor" element={<VendorLandingPage />} />
      <Route path="/gainesville-detailing-vendor" element={<VendorLandingPage />} />

      {/* Internal-only design review surfaces — robots-disallowed via /_logo_review/ in robots.txt */}
      <Route path="/_logo_animation_review" element={<LogoAnimationReviewPage />} />
      <Route path="/contractor/application-status/:applicationId" element={<ContractorApplicationStatus />} />
      
      {/* Vendor Business Onboarding - Public */}
      <Route path="/vendor-onboarding" element={<Navigate to="/vendor/apply" replace />} />
      <Route path="/vendors/onboard" element={<Navigate to="/vendor/apply" replace />} />
      {/* VOQA-P0-01 (2026-06-18) — Backward-compat alias for the
          legacy `/vendor/onboarding` URL that VendorQRPage navigated
          to before the QR-page tier-click fix. Any old QR codes,
          bookmarks, or email links pointing at the legacy URL now
          route to the canonical `/vendor/apply` with `?tier=`
          preserved instead of dying at the catch-all router. */}
      <Route path="/vendor/onboarding" element={<VendorApplyAlias />} />
      <Route path="/vendor/apply" element={<VendorOnboarding />} />
      <Route path="/earn-with-washpros" element={<VendorOnboarding />} />

      {/* VOQA-P0-02 (2026-06-18) — Stripe Connect return URL is
          deliberately PUBLIC so vendors whose session expired during
          the 10-min Stripe-hosted KYC flow get a confirmation card
          (or refresh-needed card) instead of being silently bounced
          to /welcome with no signal that their payout account is
          connected. Mounted BEFORE `/vendor/*` ProtectedRoute so
          this more-specific route wins the match. */}
      <Route path="/vendor/stripe-connect" element={<VendorStripeConnectReturn />} />

      {/* VOQA-P1-06 (2026-06-18) — Vendor supply store is now gated
          behind ProtectedRoute. Anonymous visitors used to be able to
          browse the catalog + click Add-to-Cart on 12 products. Cart
          state would have leaked into unauth localStorage and the
          investor-grade "supply-side gated" narrative was undermined.
          Unauth users now get bounced to /welcome with intendedPath
          preserved → they sign in → return to the store with their
          intent intact. Same applies to /store and /shop aliases. */}
      <Route path="/vendor/store" element={<ProtectedRoute><VendorStore /></ProtectedRoute>} />
      <Route path="/store" element={<ProtectedRoute><VendorStore /></ProtectedRoute>} />
      <Route path="/shop" element={<ProtectedRoute><VendorStore /></ProtectedRoute>} />
      
      {/* Protected Customer App routes - redirects admins/vendors to their portals */}
      <Route path="/dashboard/*" element={
        <CustomerDashboardRoute>
          <CustomerApp />
        </CustomerDashboardRoute>
      } />
      
      {/* Protected Vendor App routes */}
      <Route path="/vendor/*" element={
        <ProtectedRoute>
          <VendorApp />
        </ProtectedRoute>
      } />
      
      {/* P1.4 follow-up (2026-06-17, Rick directive): explicit
          /admin/login route. Without this, /admin/login was eaten by
          the /admin/* ProtectedRoute below and silently redirected
          unauthenticated visitors to /welcome (homepage), which was
          confusing for investors clicking an admin link. Now
          /admin/login renders the admin-themed auth page directly. */}
      <Route path="/admin/login" element={<AuthPage defaultMode="login" userType="customer" />} />
      <Route path="/admin/signup" element={<Navigate to="/admin/login" replace />} />

      {/* Protected Admin routes */}
      <Route path="/admin/*" element={
        <ProtectedRoute>
          <AdminDashboard />
        </ProtectedRoute>
      } />

      {/* Booking success page */}
      <Route path="/booking/success" element={
        <ProtectedRoute>
          <CustomerApp initialRoute="booking-success" />
        </ProtectedRoute>
      } />

      {/* Live Tracking - Customer */}
      <Route path="/booking/:bookingId/track" element={
        <ProtectedRoute>
          <CustomerLiveTrackingScreen />
        </ProtectedRoute>
      } />

      {/* Booking Confirmation - After Stripe Payment (public - user just paid) */}
      <Route path="/booking/confirmation/:bookingId" element={
          <BookingConfirmation />
      } />

      {/* Vendor Action Flow */}
      <Route path="/vendor/job/:bookingId" element={
        <ProtectedRoute>
          <VendorActionFlowScreen />
        </ProtectedRoute>
      } />

      {/* Subscription pages */}
      <Route path="/subscription/*" element={
        <ProtectedRoute>
          <SubscriptionPage />
        </ProtectedRoute>
      } />
      
      {/* Blog Pages */}
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:postId" element={<BlogPostPage />} />
      
      {/* Booking Flow - Gated behind PUBLIC_BOOKING_ENABLED (pre-launch waitlist mode) */}
      <Route path="/book" element={PUBLIC_BOOKING_ENABLED ? <SimpleBookingFlow /> : <CustomerWaitlistPage />} />
      <Route path="/book/wash" element={PUBLIC_BOOKING_ENABLED ? <SimpleBookingFlow /> : <CustomerWaitlistPage />} />
      <Route path="/book/simple" element={PUBLIC_BOOKING_ENABLED ? <SimpleBookingFlow /> : <CustomerWaitlistPage />} />
      {/* CBQA-P2-08 (2026-06-17) — `/dashboard/book` alias for the
          confirmation page's "Wash another vehicle" CTA (and any
          legacy bookmarks). Aliases to the public booking flow with
          all query params (vehicle, from_booking) preserved so the
          user never lands on a 404 or blank page. */}
      <Route path="/dashboard/book" element={<DashboardBookAlias />} />
      <Route path="/dashboard/book/*" element={<DashboardBookAlias />} />
      <Route path="/waitlist" element={<CustomerWaitlistPage />} />

      {/* VIP Membership Signup - Gated behind PUBLIC_VIP_ENABLED */}
      <Route path="/vip-signup" element={PUBLIC_VIP_ENABLED ? <VIPSignupPage /> : <CustomerWaitlistPage />} />
      <Route path="/vip" element={PUBLIC_VIP_ENABLED ? <VIPSignupPage /> : <CustomerWaitlistPage />} />
      <Route path="/membership" element={PUBLIC_VIP_ENABLED ? <VIPSignupPage /> : <CustomerWaitlistPage />} />
      <Route path="/join-vip" element={PUBLIC_VIP_ENABLED ? <VIPSignupPage /> : <CustomerWaitlistPage />} />
      
      {/* Contact Us Page - Unified support with tabs */}
      <Route path="/contact" element={<ContactUsPage />} />
      <Route path="/support" element={<ContactUsPage />} />

      {/* Public account deletion landing — required by Apple §5.1.1(v) and Google Play Data Safety */}
      <Route path="/account-deletion" element={<AccountDeletionPublic />} />
      <Route path="/delete-account" element={<AccountDeletionPublic />} />
      
      {/* Fleet — canonical product page (was duplicated; consolidated 2026-05-17 / Phase 7g.15.22) */}
      <Route path="/fleet" element={<FleetServicesPage />} />
      <Route path="/fleet-contact" element={<FleetContactForm />} />
      <Route path="/fleet-management" element={<ContactUsPage />} />
      
      {/* Post-Service Tip */}
      <Route path="/tip/:bookingId" element={<PostServiceTip />} />
      
      {/* Service History & Recurring */}
      <Route path="/history" element={<ServiceHistoryPage />} />
      
      {/* Brick & Mortar Shop Finder */}
      <Route path="/shops" element={<ShopFinder />} />
      <Route path="/find-shop" element={<ShopFinder />} />
      
      {/* Vendor Marketplace */}
      <Route path="/vendors" element={<VendorCoveragePage />} />
      <Route path="/find-washpro" element={<VendorMarketplace />} />
      <Route path="/marketplace" element={<VendorMarketplace />} />
      
      {/* Video Gallery */}
      <Route path="/videos" element={<VideoGalleryPage />} />
      
      {/* Pricing Page */}
      <Route path="/pricing" element={<PricingPage />} />
      
      {/* Service Pages */}
      <Route path="/services/car-wash" element={<CarWashServicesPage />} />
      <Route path="/services/car" element={<CarWashServicesPage />} />
      <Route path="/services/boat" element={<BoatServicesPage />} />
      <Route path="/services/boat-detailing" element={<BoatServicesPage />} />
      <Route path="/services/rv" element={<RVServicesPage />} />
      <Route path="/services/rv-washing" element={<RVServicesPage />} />
      <Route path="/services/fleet" element={<FleetServicesPage />} />
      {/* /fleet declared higher up — leaving here as duplicate-safe path; React Router uses first match. */}
      <Route path="/services/ceramic-coating" element={<CeramicCoatingPage />} />
      <Route path="/services/ceramic" element={<CeramicCoatingPage />} />
      <Route path="/services/commercial-fleet" element={<CommercialFleetPage />} />
      <Route path="/commercial-fleet" element={<CommercialFleetPage />} />
      
      {/* About Us Page */}
      <Route path="/about" element={<AboutUsPage />} />
      <Route path="/system-status" element={<PublicStatusPage />} />
      <Route path="/faq" element={<FAQPage />} />

      {/* Brand Asset Lock — admin-only brand-usage guide + admin-only downloads page (moved out of public eye 2026-05-16) */}
      {/* /brand-usage + /brand-assets now live at /admin/brand-* (admin-gated). Public routes redirect for stale-bookmark safety. */}
      <Route path="/brand-usage" element={<Navigate to="/admin/brand-usage" replace />} />
      <Route path="/brand-assets" element={<Navigate to="/admin/brand-assets" replace />} />
      
      {/* Legal Pages */}
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      {/* Phase 7g.15.48 — Twilio A2P CTA proof page */}
      <Route path="/sms-consent" element={<SmsConsentPage />} />
      {/* Phase 7g.15.15 — Copyright Infringement Notification Policy */}
      <Route path="/copyright-policy" element={<CopyrightPolicyPage />} />

      {/* Phase 7g.15.20i — Full WashPros Legal & Trust Policy Center
          (Rick directive 2026-05-16, post Florida DBA filing) */}
      <Route path="/legal" element={<LegalHubPage />} />
      <Route path="/acceptable-use" element={<AcceptableUsePage />} />
      <Route path="/vendor-marketplace-standards" element={<VendorMarketplaceStandardsPage />} />
      <Route path="/booking-refund-dispute-policy" element={<BookingRefundDisputePolicyPage />} />
      <Route path="/communications-policy" element={<CommunicationsPolicyPage />} />
      <Route path="/data-security" element={<DataSecurityPage />} />
      <Route path="/cookie-policy" element={<CookiePolicyPage />} />
      <Route path="/data-retention" element={<DataRetentionPage />} />
      <Route path="/reviews-policy" element={<ReviewsPolicyPage />} />
      <Route path="/influencer-disclosure" element={<InfluencerDisclosurePage />} />
      <Route path="/trademark-policy" element={<TrademarkPolicyPage />} />
      <Route path="/subscription-terms" element={<SubscriptionTermsPage />} />
      <Route path="/vendor-payout-policy" element={<VendorPayoutPolicyPage />} />
      <Route path="/ai-disclosure" element={<AIDisclosurePage />} />
      <Route path="/photo-media-policy" element={<PhotoMediaPolicyPage />} />
      <Route path="/property-damage-policy" element={<PropertyDamagePolicyPage />} />
      <Route path="/dmca-agent" element={<DMCAAgentPage />} />
      
      {/* Vendor Agreement Pages */}
      <Route path="/legal/agreements" element={<VendorAgreementsHub />} />
      <Route path="/legal/independent-contractor" element={<IndependentContractorAgreement />} />
      <Route path="/legal/mobile-vendor" element={<MobileVendorBusinessAgreement />} />
      <Route path="/legal/brick-and-mortar" element={<BrickAndMortarShopAgreement />} />
      <Route path="/legal/vendor-equipment-list" element={<VendorEquipmentListPage />} />
      
      {/* Email Auth Pages - Premium Auth as default */}
      <Route path="/auth/login" element={<PremiumAuthPage />} />
      <Route path="/auth/signup" element={<PremiumAuthPage />} />
      <Route path="/login" element={<PremiumAuthPage />} />
      <Route path="/signup" element={<PremiumAuthPage />} />
      <Route path="/auth/legacy/login" element={<AuthPage defaultMode="login" userType="customer" />} />
      <Route path="/auth/legacy/signup" element={<AuthPage defaultMode="signup" userType="customer" />} />
      <Route path="/auth/vendor/login" element={<AuthPage defaultMode="login" userType="vendor" />} />
      <Route path="/auth/vendor/signup" element={<AuthPage defaultMode="signup" userType="vendor" />} />
      <Route path="/auth/magic-link" element={<MagicLinkCallback />} />
      <Route path="/auth/reset-password" element={<PasswordResetPage />} />
      <Route path="/setup-password" element={<SetupPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/native-bridge" element={<NativeAuthBridge />} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

// New User Review Prompt Wrapper
const NewUserReviewPrompt = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    // Phase 7g.15.98 — QA escape hatch. Appending
    // `?demo_review_prompt=1` to any URL force-opens the prompt for
    // visual verification at any viewport, bypassing the signup-date
    // / cooldown gates. Safe to leave in prod — discoverable only by
    // Rick / Dan / QA via the query param. Handled here at the top
    // of the existing trigger effect so we don't fight the rest of
    // the gate logic.
    try {
      const params = new URLSearchParams(location.search);
      if (params.get("demo_review_prompt") === "1") {
        setShowPrompt(true);
        return;
      }
    } catch (_e) {
      /* ignore URL parse errors */
    }

    // Don't show during booking flow
    if (location.pathname.startsWith('/book')) {
      setShowPrompt(false);
      return;
    }

    // Don't show on review/closeout pages — would feel like a
    // duplicate prompt right after the customer just left a
    // vendor review (Rick QA 2026-05-08).
    if (
      location.pathname.includes('/review') ||
      location.pathname.includes('/closeout') ||
      location.pathname.includes('/rate')
    ) {
      setShowPrompt(false);
      return;
    }

    // Check if this is a new user (signed up recently)
    if (!user) return;

    // Only customers see the platform review prompt — vendors and
    // admins should never get it (legacy shim: master_admin Rick
    // was getting prompted because role check below was missing
    // earlier).
    if (user.role !== "customer") return;

    const prompted = localStorage.getItem("washpros_review_prompted");
    const signupDate = localStorage.getItem("washpros_signup_date");

    // 24h cooling-off after a vendor review submission — prevents
    // back-to-back vendor-review + platform-review pairs.
    const lastVendorReviewAt = localStorage.getItem(
      "washpros_last_vendor_review_at"
    );
    if (lastVendorReviewAt) {
      const hoursSince =
        (Date.now() - new Date(lastVendorReviewAt).getTime()) / 36e5;
      if (hoursSince < 24) {
        setShowPrompt(false);
        return;
      }
    }

    // If user just signed up (no signup date stored yet), store it
    if (!signupDate) {
      localStorage.setItem("washpros_signup_date", new Date().toISOString());
      // Show review prompt after 5 seconds for new users
      const timer = setTimeout(() => {
        if (!prompted) {
          setShowPrompt(true);
        }
      }, 5000);
      return () => clearTimeout(timer);
    }

    // For existing users, check if should show based on time since signup
    if (signupDate && !prompted) {
      const daysSinceSignup = (Date.now() - new Date(signupDate).getTime()) / (1000 * 60 * 60 * 24);
      // Show after 3 days if not prompted yet
      if (daysSinceSignup >= 3) {
        setShowPrompt(true);
      }
    }
  }, [user, location.pathname]);
  
  // Phase 7g.15.98 — when `?demo_review_prompt=1` is in the URL we
  // bypass the user-required gate too, so the prompt renders even
  // on an anonymous browser for visual verification.
  const isDemoMode = (() => {
    try {
      return new URLSearchParams(window.location.search).get("demo_review_prompt") === "1";
    } catch (_e) {
      return false;
    }
  })();

  if (!showPrompt) return null;
  if (!user && !isDemoMode) return null;

  return (
    <PlatformReviewPrompt
      isOpen={showPrompt}
      onClose={() => setShowPrompt(false)}
      customerName={user?.name?.split(' ')[0] || 'there'}
    />
  );
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <LanguageProvider>
          <FirefoxWarning />
          <LanguageAutoDetectToast />
          <AuthProvider>
            <AppRouter />
            <NewUserReviewPrompt />
            <Suspense fallback={null}>
              <SupportChatbot />
            </Suspense>
            <Suspense fallback={null}>
              {/* Beta welcome — visible only on native TestFlight/Play
                  internal builds with REACT_APP_TESTING_MODE=true. */}
              <BetaWelcomeCard />
            </Suspense>
            {/* Phase 7g.15.48 (Rick 2026-05-27): BetaBuildBadge fully
                retired — caused a persistent overlay at #root > div:nth-child(3)
                on production. Re-introduce only via a new component if
                a debug badge is ever needed again. */}
            {/* Global toast surface — sonner.Toaster must be mounted
                for toast.error() / toast.success() to appear on screen. */}
            <Toaster
              position="top-center"
              richColors
              closeButton
              toastOptions={{ duration: 6000 }}
            />
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;